import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// CUSTDISP v2: click opens /customers-dispatched
import { getCustomersDispatched } from '../services/dashboardApi';
import CustomerHistoryModal from './CustomerHistoryModal';

const BORDER = '1px solid var(--color-border)';
const th = { padding: '6px 10px', fontSize: 'var(--font-size-xs)', fontWeight: 700, textAlign: 'left', background: '#e2e8f0', color: '#1e293b', whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', fontSize: 'var(--font-size-sm)', borderTop: BORDER };

/** Dashboard card: number of customers dispatched in the current month.
 * Click -> list with their dispatched boxes -> click a customer -> that customer's
 * report for the current month. Data is already limited per Ref. Party by the backend. */
export default function CustomersDispatchedCard({ Kpi }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let dead = false;
    getCustomersDispatched().then((d) => { if (!dead) setData(d); }).catch(() => {});
    return () => { dead = true; };
  }, []);

  useEffect(() => {
    if (!open || active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active]);

  if (!data) return null;
  const s = q.trim().toLowerCase();
  const items = s ? data.items.filter((i) => `${i.name} ${i.city || ''}`.toLowerCase().includes(s)) : data.items;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="Number of customers dispatched"
        title={`${data.label} · ${data.totalBoxes.toLocaleString('en-IN')} box — click to see the customers`}
        onClick={() => navigate('/customers-dispatched')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/customers-dispatched'); } }}
        className="cust-kpi-wrap"
        style={{ display: 'flex', cursor: 'pointer', boxSizing: 'border-box' }}
      >
        <Kpi label="Number of Customers Dispatched" value={data.count} />
      </div>

      {open && (
        <div
          role="dialog"
          aria-label="Customers dispatched this month"
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 900, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 24 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#f8fafc', borderRadius: 10, width: 'min(760px, 100%)', padding: 16, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{data.label}</div>
                <h3 style={{ margin: 0 }}>Number of Customers Dispatched — {data.count}</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕ Close</button>
            </div>
            <input
              type="search"
              placeholder="Find customer or city…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ margin: '10px 0', padding: '6px 10px', width: '100%', maxWidth: 320, border: BORDER, borderRadius: 6, font: 'inherit' }}
            />
            <div className="table-scroll" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Customer</th>
                    <th style={th}>City</th>
                    <th className="data-figure" style={{ ...th, textAlign: 'right' }}>Dispatched Box</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i, idx) => (
                    <tr key={i.id}>
                      <td style={td}>{idx + 1}</td>
                      <td style={td}>
                        <button
                          type="button"
                          onClick={() => setActive({ id: i.id, name: i.name, from: data.from, to: data.to })}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', fontWeight: 600, textDecoration: 'underline', font: 'inherit', textAlign: 'left' }}
                        >
                          {i.name}
                        </button>
                      </td>
                      <td style={td}>{i.city || '—'}</td>
                      <td className="data-figure" style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{i.boxes.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={4} style={{ ...td, color: 'var(--color-text-muted)' }}>No customer found.</td></tr>}
                  {items.length > 0 && (
                    <tr style={{ background: '#e2e8f0', fontWeight: 700 }}>
                      <td style={td} colSpan={3}>Total ({items.length} customers)</td>
                      <td className="data-figure" style={{ ...td, textAlign: 'right' }}>{items.reduce((t, i) => t + i.boxes, 0).toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {active && <CustomerHistoryModal customer={active} onClose={() => setActive(null)} />}
    </>
  );
}
